import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { cardsAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Plus, CreditCard, Loader2, Edit2, Trash2, MoreVertical } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const CardsPage = () => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCards();
  }, []);

  const fetchCards = async () => {
    try {
      const { data } = await cardsAPI.list();
      setCards(data);
    } catch (error) {
      console.error('Failed to fetch cards:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await cardsAPI.delete(deleteId);
      setCards(cards.filter(c => c.id !== deleteId));
    } catch (error) {
      console.error('Failed to delete card:', error);
    }
    setDeleteId(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Cabinet Grotesk' }}>
            Mes cartes
          </h1>
          <p className="text-slate-600 mt-1">Créez et gérez vos cartes de fidélité numériques</p>
        </div>
        <Link to="/dashboard/cards/new">
          <Button className="bg-blue-600 hover:bg-blue-700" data-testid="create-card-btn">
            <Plus className="w-4 h-4 mr-2" /> Créer une carte
          </Button>
        </Link>
      </div>

      {cards.length > 0 ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {cards.map((card) => (
            <Card key={card.id} className="border-slate-200 card-hover overflow-hidden cursor-pointer" data-testid={`card-${card.id}`} onClick={() => navigate(`/dashboard/cards/${card.id}`)}>
              {/* Apple Wallet Mini Preview */}
              <div 
                className="relative overflow-hidden"
                style={{ backgroundColor: card.primary_color }}
              >
                {/* Header */}
                <div className="flex items-center justify-between px-3 py-2" style={{ color: card.secondary_color || '#fff' }}>
                  <div className="flex items-center gap-2 min-w-0">
                    {card.logo_url ? (
                      <img src={card.logo_url} alt="Logo" className="w-7 h-7 rounded-md object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-7 h-7 rounded-md bg-white/20 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-bold">{card.name?.charAt(0) || 'L'}</span>
                      </div>
                    )}
                    <span className="font-semibold text-xs truncate">{card.name}</span>
                  </div>
                  <div className="text-right flex-shrink-0 ml-1">
                    <p className="text-[8px] uppercase opacity-70">
                      {card.card_type === 'stamps' ? 'TAMPONS' : 'POINTS'}
                    </p>
                    <p className="text-sm font-bold leading-tight">
                      {card.card_type === 'stamps' ? `0/${card.total_stamps||10}` : '0'}
                    </p>
                  </div>
                </div>
                {/* Strip area */}
                <div className="h-20" style={{ background: `linear-gradient(135deg, ${card.primary_color}ee 0%, ${card.primary_color}99 100%)` }}>
                  {card.strip_url ? (
                    <img src={card.strip_url} alt="" className="w-full h-full object-cover" />
                  ) : card.background_url && card.background_url !== 'gradient' ? (
                    <img src={card.background_url} alt="" className="w-full h-full object-cover" />
                  ) : null}
                </div>
              </div>

              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    {card.description && <p className="text-sm text-slate-500">{card.description}</p>}
                    <p className="text-xs text-slate-400 mt-1">
                      Créée le {new Date(card.created_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`card-menu-${card.id}`}>
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => navigate(`/dashboard/cards/${card.id}`)}>
                        <Edit2 className="w-4 h-4 mr-2" /> Modifier
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => setDeleteId(card.id)}
                        className="text-red-600"
                      >
                        <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border-slate-200">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
              <CreditCard className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Aucune carte de fidélité</h3>
            <p className="text-slate-500 text-center mb-6 max-w-sm">
              Créez votre première carte pour commencer à fidéliser vos clients
            </p>
            <Link to="/dashboard/cards/new">
              <Button className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" /> Créer ma première carte
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette carte ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Les soldes et transactions des clients seront conservés mais la carte ne sera plus active.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CardsPage;
